import { Button, Group, Pill, Stack } from "@mantine/core";
import { useState } from "react";
import { useParams } from "react-router";
import { NoDataMessage } from "../../../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { EnergyDataAcquisitionSelectionModal } from "../../EnergyDataAcquisitionSelectionModal/EnergyDataAcquisitionSelectionModal";
import type { Acquisition, AcquisitionFilter } from "../../EnergyDataView";
import { useEnergyDataAcquisitionFilterOptions } from "./useEnergyDataAcquisitionFilterOptions";
import { useEnergyDataAcquisitionFilters } from "./useEnergyDataAcquisitionFilters";

interface AcquisitionsProps {
  acquisitions: Array<Acquisition>;
  selectedAcquisitions: Array<number>;
  acquisitionFilters: Array<AcquisitionFilter>;
  definedFilterId: number | null;
  variantId: number;
  onSelectedAcquisitionsChanged: (
    selectedAcquisitions: Array<number>,
    acquisitionFilters: Array<AcquisitionFilter>,
    definedFilterId: number | null
  ) => void;
}

function Acquisitions({
  acquisitions,
  selectedAcquisitions,
  acquisitionFilters,
  definedFilterId,
  variantId,
  onSelectedAcquisitionsChanged
}: AcquisitionsProps) {
  const { projectId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  function removeAcquisition(acquisitionId: number): void {
    onSelectedAcquisitionsChanged(
      selectedAcquisitions.filter(
        (selectedAcquisitionId) => selectedAcquisitionId !== acquisitionId
      ),
      acquisitionFilters,
      definedFilterId
    );
  }

  const selectedAcquisitionObjects = acquisitions.filter((acquisition) =>
    selectedAcquisitions.includes(acquisition.id)
  );

  const { data: filterOptions, isLoading: isLoadingfilterOptions } =
    useEnergyDataAcquisitionFilterOptions(projectId);
  const { data: definedFilters, isLoading: isLoadingDefinedFilters } =
    useEnergyDataAcquisitionFilters(projectId);

  const isModalLoading = isLoadingfilterOptions || isLoadingDefinedFilters;

  return (
    <>
      <Stack
        align={selectedAcquisitions.length === 0 ? "center" : "flex-start"}
        className="Acquisitions"
        gap="md"
      >
        {selectedAcquisitions.length === 0 && (
          <NoDataMessage
            message="Es wurden noch keine Daten für die Anzeige ausgewählt. Wählen Sie
           hier einen Betrachtungszeitraum sowie die gewünschten Energiedaten aus."
          />
        )}
        <Button
          color="primary"
          loading={isModalLoading}
          size={selectedAcquisitions.length === 0 ? "lg" : undefined}
          onClick={() => setIsModalOpen(true)}
        >
          Energiedaten auswählen
        </Button>
        {selectedAcquisitionObjects.length > 0 && (
          <Group gap="xs">
            {selectedAcquisitionObjects.map((acquisition) => (
              <Pill
                key={acquisition.id}
                withRemoveButton
                onRemove={() => removeAcquisition(acquisition.id)}
              >
                {acquisition.label}
              </Pill>
            ))}
          </Group>
        )}
      </Stack>
      {projectId && filterOptions && definedFilters && (
        <EnergyDataAcquisitionSelectionModal
          data={{
            filterOptions,
            definedFilters
          }}
          initialAcquisitionFilters={acquisitionFilters}
          initialDefinedFilterId={definedFilterId}
          initialSelectedAcquisitions={selectedAcquisitions}
          isOpen={isModalOpen}
          projectId={projectId}
          variantId={variantId}
          onSelectedAcquisitionsChanged={onSelectedAcquisitionsChanged}
          onToggle={() => setIsModalOpen(!isModalOpen)}
        />
      )}
    </>
  );
}

export { Acquisitions };
